import { useState, useRef, useCallback, useEffect } from 'react'
import { useToast } from '@/components/providers/ToastProvider'
import { useWakeLock } from '@/hooks/useWakeLock'
import { useRecordingKeepalive } from '@/hooks/useRecordingKeepalive'
import { selectCodec } from '@/lib/audio/codecSelector'
import { probeMicPermission } from '@/lib/audio/permissions'
import {
  MediaRecorderSession,
  type RecordedChunk,
} from '@/lib/audio/mediaRecorderSession'

export type { RecordedChunk }

interface UseRecorderOptions {
  onRecordingStart?: () => void
  onRecordingStop?: (payload: {
    blob: Blob
    durationMs: number
    chunks: RecordedChunk[]
  }) => void
  onChunkAvailable?: (chunk: RecordedChunk) => Promise<void> | void
}

export function useRecorder(options: UseRecorderOptions = {}) {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [durationMs, setDurationMs] = useState(0)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sessionRef = useRef<MediaRecorderSession | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const startTimeRef = useRef<number>(0)
  const durationIntervalRef = useRef<number | null>(null)
  const { showToast } = useToast()

  // Keep the screen awake while actively capturing audio.
  useWakeLock(isRecording && !isPaused)

  // Play inaudible audio + register Media Session so the browser treats this
  // tab as actively using audio.
  useRecordingKeepalive(isRecording && !isPaused)

  // When the tab is hidden (app switch, phone call, screen off), force the
  // current chunk to roll up immediately so the OS can suspend JS without
  // losing audio in memory.
  useEffect(() => {
    if (!isRecording) return

    const persistChunkNow = () => {
      sessionRef.current?.persistChunkNow()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        persistChunkNow()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', persistChunkNow)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', persistChunkNow)
    }
  }, [isRecording])

  const checkPermission = useCallback(async () => {
    const result = await probeMicPermission()
    if (result.ok) {
      setHasPermission(true)
      setError(null)
      return
    }
    setHasPermission(false)
    setError(result.message)
    if (result.reason === 'denied') {
      showToast('Microphone permission denied. Please enable microphone access.', 'error')
    } else if (result.reason === 'not-found') {
      showToast('No microphone found. Please connect a microphone.', 'error')
    }
  }, [showToast])

  // Check microphone permission on mount
  useEffect(() => {
    void checkPermission()
  }, [checkPermission])

  const stopDurationTicker = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }
  }, [])

  const startDurationTicker = useCallback(() => {
    stopDurationTicker()
    durationIntervalRef.current = window.setInterval(() => {
      setDurationMs(Date.now() - startTimeRef.current)
    }, 100)
  }, [stopDurationTicker])

  const stopRecording = useCallback(() => {
    if (sessionRef.current && isRecording) {
      sessionRef.current.stop()
      setIsRecording(false)
      setIsPaused(false)
      stopDurationTicker()
    }
  }, [isRecording, stopDurationTicker])

  const startRecording = useCallback(async () => {
    if (!hasPermission) {
      await checkPermission()
      if (!hasPermission) {
        return
      }
    }

    try {
      // MediaRecorder works offline - no internet connection needed
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })

      streamRef.current = stream

      const { mimeType, bitrate } = selectCodec()

      const session = new MediaRecorderSession({
        stream,
        mimeType,
        bitrate,
        onChunk: options.onChunkAvailable,
        onRecordingStopped: (payload) => {
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop())
            streamRef.current = null
          }
          stopDurationTicker()
          options.onRecordingStop?.(payload)
        },
        onError: (event) => {
          console.error('MediaRecorder error:', event)
          showToast('Recording error occurred. Please try again.', 'error')
          stopRecording()
        },
      })

      sessionRef.current = session
      startTimeRef.current = Date.now()
      session.start()

      setIsRecording(true)
      setIsPaused(false)
      setDurationMs(0)
      setError(null)

      startDurationTicker()

      options.onRecordingStart?.()
    } catch (err) {
      console.error('Failed to start recording:', err)
      setError(err instanceof Error ? err.message : 'Failed to start recording')
      if (err instanceof Error && err.name === 'NotAllowedError') {
        showToast('Microphone permission denied. Please enable microphone access.', 'error')
      } else {
        showToast('Failed to start recording. Please try again.', 'error')
      }
    }
  }, [
    hasPermission,
    options,
    showToast,
    checkPermission,
    startDurationTicker,
    stopDurationTicker,
    stopRecording,
  ])

  const pauseRecording = useCallback(() => {
    if (sessionRef.current && isRecording && !isPaused) {
      sessionRef.current.pause()
      setIsPaused(true)
      stopDurationTicker()
    }
  }, [isRecording, isPaused, stopDurationTicker])

  const resumeRecording = useCallback(() => {
    if (sessionRef.current && isRecording && isPaused) {
      sessionRef.current.resume()
      setIsPaused(false)
      startTimeRef.current = Date.now() - durationMs
      startDurationTicker()
    }
  }, [isRecording, isPaused, durationMs, startDurationTicker])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
      }
    }
  }, [])

  return {
    isRecording,
    isPaused,
    durationMs,
    hasPermission,
    error,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    checkPermission,
  }
}
